import { Text } from '@ebh/capsule';
import styles from './styles';
import { Person } from '@vhc-portal/common/components';
import { EncryptedIcon, HomeFilledIcon, LogoutIcon } from '@ebh/capsule/icons';
import cookiesManager from '../../utils/cookies';
import { USER_DETAILS } from '../../utils/cookies/keys';
import { useScreen } from '@ebh/capsule/hooks';
import { useNavigate } from 'react-router-dom';
import { handleLogout } from '../../utils/logout';
import { downloadRemoteFile, resolveCmsUrl } from '../../utils/download-file';
import useGetCmsData from '../../api/hooks/cms/use-get-cms-data';
import { queryClient } from '../../api/query-client';
import FileSave from './assets/file_save.svg';

const ICON_MAP: Record<string, string> = {
  FileSave,
};

interface ProfileBullet {
  id: number;
  Title: string;
  MetaData: {
    url: string;
    icon?: string;
    newTab?: boolean;
    showInProfile?: boolean;
    download?: boolean;
  };
}

interface ProfileSelectionProps {
  onAction?: () => void;
  setIsChangePasswordOpen: (arg0: boolean) => void;
}

export const ProfileSelection = ({
  onAction,
  setIsChangePasswordOpen,
}: ProfileSelectionProps) => {
  const isMobile = !useScreen('md');
  const fullName = cookiesManager.get(USER_DETAILS.FULL_NAME);
  const navigate = useNavigate();
  const handleMenuClick = (callback?: () => void) => {
    if (callback) callback();
    if (onAction) onAction();
  };
  const isSso = !!cookiesManager.get(USER_DETAILS.IS_SSO);
  const sourceType = cookiesManager.get(USER_DETAILS.SOURCE_TYPE);
  const canChangePassword = (!isSso || (isSso && sourceType === 'OLD_HR_PORTAL'));

  const params = {
    pageType: 'HR_Landing Page',
    pageName: 'HR_Sidebar',
    collectionType: 'vidal-product-page-layout',
  };
  const cachedData = queryClient.getQueryData(['cmsData', params]);
  const { cmsData } = useGetCmsData({ params, enabled: !cachedData });

  const pageLayout =
    (cmsData ?? (cachedData as typeof cmsData))?.data?.[0]?.PageLayout ?? [];

  const profileBullets: ProfileBullet[] = pageLayout.flatMap(
    (section: { Bullets?: ProfileBullet[] }) =>
      section?.Bullets?.filter(
        bullet => bullet?.MetaData?.showInProfile && bullet?.Title && bullet?.MetaData?.url,
      ) ?? [],
  );

  const handleBulletClick = (bullet: ProfileBullet) => {
    const { newTab, download } = bullet.MetaData;
    const url = resolveCmsUrl(bullet.MetaData.url);
    if (download) {
      const filename = url.split('/').pop()?.split('?')[0] || bullet.Title;
      downloadRemoteFile({ url, filename });
      return;
    }
    if (newTab || /^https?:\/\//.test(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(url);
  };

  return (
    <>
      <div css={styles.container}>
        <div css={styles.userProfile}>
          <Person
            color="gray-500"
            size={isMobile ? 45 : 24}
            css={styles.userProfileIcon}
          />
          <Text variant="body-2" color="black">
            {fullName}
          </Text>
        </div>
        <div css={styles.menu}>
          {isMobile && (
            <div
              css={styles.menuItem}
              onClick={() => {
                handleMenuClick(() => navigate('/'));
              }}
            >
              <HomeFilledIcon color="black" css={styles.menuIcon} />
              <Text variant="body-1" color="gray-500">
                Homepage
              </Text>
            </div>
          )}
          {profileBullets.map(bullet => {
            const icon = bullet.MetaData.icon
              ? ICON_MAP[bullet.MetaData.icon]
              : undefined;
            return (
              <div
                key={bullet.id}
                css={styles.menuItem}
                onClick={() => handleMenuClick(() => handleBulletClick(bullet))}
              >
                {icon && <img src={icon} alt={bullet.Title} css={styles.menuIcon} />}
                <Text variant="body-1" color="gray-500">
                  {bullet.Title}
                </Text>
              </div>
            );
          })}
          {canChangePassword && (
            <div
              css={styles.menuItem}
              onClick={() =>
                handleMenuClick(() => setIsChangePasswordOpen(true))
              }
            >
              <EncryptedIcon color="black" css={styles.menuIcon} />
              <Text variant="body-1" color="gray-500">
                Change Password
              </Text>
            </div>
          )}

          {canChangePassword && (
            <div css={styles.menuItem} onClick={handleLogout}>
              <LogoutIcon color="black" css={styles.menuIcon} />
              <Text variant="body-1" color="gray-500">
                Log out
              </Text>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
